import { useEffect, useMemo, useRef } from "react";
import KZ_RAW from "../assets/globe/kz-border.json";
import WORLD_RAW from "../assets/globe/world.json";
import KZ_OBLASTS from "../assets/globe/kz-oblasts.json";

// ─────────────────────────────────────────────────────────────────────────────
// Глобус с аналитикой по областям Казахстана.
// Адаптация HeroAnimation из otbasy_hresto: вместо клубов — choropleth по метрике
// (пользователи / объявления / просмотры), tooltip со статистикой региона.
// ─────────────────────────────────────────────────────────────────────────────

const DEG = Math.PI / 180;
const AY_BASE = -18 * DEG; // камера центрі: ay+90 = 72° — KZ центроиды (lon shift-тен кейін)
const AX = 48 * DEG;       // көлбеу KZ центроидының ендігіне тең
const KZ_ALT = 0.022;

const KZ_CENTROID = [68, 48];
const KZ_SCALE = 1.15;
const KZ_LON_SHIFT = 4;

const KZ_POLY = KZ_RAW.map(([lon, lat]) => [
  KZ_CENTROID[0] + (lon - KZ_CENTROID[0]) * KZ_SCALE + KZ_LON_SHIFT,
  KZ_CENTROID[1] + (lat - KZ_CENTROID[1]) * KZ_SCALE,
]);

function ll2xyz(lonDeg, latDeg) {
  const lat = latDeg * DEG, lon = lonDeg * DEG;
  const cl = Math.cos(lat);
  return [cl * Math.cos(lon), Math.sin(lat), cl * Math.sin(lon)];
}

// 2D point-in-polygon (null = behind globe, skip)
function pip2d(mx, my, pts) {
  let inside = false, j = pts.length - 1;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[j];
    if (a && b) {
      const [xi, yi] = a, [xj, yj] = b;
      if ((yi > my) !== (yj > my) && mx < ((xj - xi) * (my - yi)) / (yj - yi) + xi)
        inside = !inside;
    }
    j = i;
  }
  return inside;
}

// Module-level precomputed data
const KZ_BASE_3D = KZ_POLY.map(([lon, lat]) => ll2xyz(lon, lat));
const WORLD_3D = WORLD_RAW.map((ring) => ring.map(([lon, lat]) => ll2xyz(lon, lat)));

const SEGS = 36;
const GRID_3D = [];
for (let lat = -75; lat <= 75; lat += 25) {
  GRID_3D.push({
    isEquator: lat === 0,
    pts: Array.from({ length: SEGS + 1 }, (_, i) => ll2xyz((i / SEGS) * 360 - 180, lat)),
  });
}
for (let lon = 0; lon < 360; lon += 30) {
  GRID_3D.push({
    isEquator: false,
    pts: Array.from({ length: SEGS + 1 }, (_, i) => ll2xyz(lon, (i / SEGS) * 180 - 90)),
  });
}

// Oblast rings at sphere surface (no elevation)
function extractRings(geojson) {
  const rings = [];
  const featureIdx = [];
  geojson.features.forEach((f, fi) => {
    const { type, coordinates } = f.geometry;
    if (type === "Polygon") {
      rings.push(coordinates[0]);
      featureIdx.push(fi);
    } else if (type === "MultiPolygon") {
      for (const poly of coordinates) {
        rings.push(poly[0]);
        featureIdx.push(fi);
      }
    }
  });
  return { rings, featureIdx };
}

const _extracted = extractRings(KZ_OBLASTS);
const KZ_OBLAST_BASE_3D = _extracted.rings.map((ring) =>
  ring.map(([lon, lat]) => {
    const tLon = KZ_CENTROID[0] + (lon - KZ_CENTROID[0]) * KZ_SCALE + KZ_LON_SHIFT;
    const tLat = KZ_CENTROID[1] + (lat - KZ_CENTROID[1]) * KZ_SCALE;
    return ll2xyz(tLon, tLat);
  })
);
const OBLAST_FEATURE_IDX = _extracted.featureIdx;
const N_OBLASTS = KZ_OBLAST_BASE_3D.length;

// ── Регион (БД) → feature глобуса ────────────────────────────────────────────
// GeoJSON облыстары 2022 жылғы реформадан бұрынғы шекаралармен келеді:
// Жетісу → Алматы облысы (0), Ұлытау → Қарағанды облысы (8),
// Абай → Шығыс Қазақстан облысы (4) ішіне қосылады.
const REGION_ID_TO_FEATURE = {
  2: 15, 3: 0, 4: 16, 5: 4, 6: 1, 7: 2, 8: 3, 9: 12, 10: 13, 11: 0,
  12: 8, 13: 9, 14: 10, 15: 5, 16: 7, 17: 6, 18: 11, 19: 8, 20: 4, 21: 14,
};
export { REGION_ID_TO_FEATURE };
const REGION_NAME_TO_FEATURE = {
  "город алматы": 15, "алматы қаласы": 15, "алматы": 15,
  "алматинская область": 0, "алматы облысы": 0, "жетысуская область": 0, "жетісу облысы": 0,
  "город астана": 16, "астана қаласы": 16, "астана": 16, "нур-султан": 16,
  "акмолинская область": 1, "ақмола облысы": 1,
  "актюбинская область": 2, "ақтөбе облысы": 2,
  "атырауская область": 3, "атырау облысы": 3,
  "западно-казахстанская область": 12, "батыс қазақстан облысы": 12,
  "жамбылская область": 13, "жамбыл облысы": 13,
  "карагандинская область": 8, "қарағанды облысы": 8, "улытауская область": 8, "ұлытау облысы": 8,
  "костанайская область": 9, "қостанай облысы": 9,
  "кызылординская область": 10, "қызылорда облысы": 10,
  "мангистауская область": 5, "маңғыстау облысы": 5,
  "павлодарская область": 7, "павлодар облысы": 7,
  "северо-казахстанская область": 6, "солтүстік қазақстан облысы": 6,
  "туркестанская область": 11, "түркістан облысы": 11,
  "восточно-казахстанская область": 4, "шығыс қазақстан облысы": 4,
  "абайская область": 4, "абай облысы": 4,
  "город шымкент": 14, "шымкент қаласы": 14, "шымкент": 14,
};

const METRIC_LABELS = {
  users: "Пользователи",
  announcements: "Объявления",
  views: "Просмотры",
};

// Choropleth палитрасы (индиго — админ-панельдің басты түсі)
const LIGHT = {
  worldFill: "rgba(224, 231, 255, 0.95)",
  grid: "99, 102, 241",
  zero: [209, 213, 219],   // gray-300
  max: [79, 70, 229],      // indigo-600
  border: "rgba(67, 56, 202, 0.9)",
};
const DARK = {
  worldFill: "rgba(99, 102, 241, 0.10)",
  grid: "129, 140, 248",
  zero: [51, 65, 85],      // slate-700
  max: [129, 140, 248],    // indigo-400
  border: "rgba(129, 140, 248, 0.9)",
};

function lerpColor(c1, c2, t) {
  return `rgb(${Math.round(c1[0] + (c2[0] - c1[0]) * t)},${Math.round(
    c1[1] + (c2[1] - c1[1]) * t
  )},${Math.round(c1[2] + (c2[2] - c1[2]) * t)})`;
}

function parseRgb(color) {
  const m = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  return m ? m.slice(1).map(Number) : [79, 70, 229];
}

function darken(rgbStr, k = 0.55) {
  const [r, g, b] = parseRgb(rgbStr);
  return `rgb(${Math.round(r * k)},${Math.round(g * k)},${Math.round(b * k)})`;
}

const FRAME_MS = 1000 / 60;
const ANIM_SPD = 7; // oblast pop-up speed (exponential approach)

function AnalyticsGlobe({ data = [], metric = "users", dark = false, highlight = -1, onSelect }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastFrameRef = useRef(0);
  const prevTimeRef = useRef(null);
  const mouseRef = useRef(null); // {x,y} in canvas CSS pixels
  const hoveredRef = useRef(-1);
  const oblastElev = useRef(new Float32Array(N_OBLASTS));
  const tooltipRef = useRef(null);
  const lastTooltipFeature = useRef(-1);
  const darkRef = useRef(dark);
  const cssSizeRef = useRef({ w: 0, h: 0 });
  const highlightRef = useRef(highlight);
  const metricRef = useRef(metric);
  const statsRef = useRef(null);

  if (darkRef.current !== dark) darkRef.current = dark;
  if (highlightRef.current !== highlight) highlightRef.current = highlight;
  if (metricRef.current !== metric) {
    metricRef.current = metric;
    lastTooltipFeature.current = -1; // force tooltip rebuild with new metric
  }

  // region_id / атау бойынша feature-ге агрегаттау
  const stats = useMemo(() => {
    const perFeature = new Map(); // featureIdx → { value, regions: [row] }
    for (const row of data) {
      const fi =
        REGION_ID_TO_FEATURE[row.region_id] ??
        REGION_NAME_TO_FEATURE[String(row.name_ru || "").toLowerCase().trim()] ??
        REGION_NAME_TO_FEATURE[String(row.name_kz || "").toLowerCase().trim()];
      if (fi === undefined) continue;
      const agg = perFeature.get(fi) || { value: 0, regions: [] };
      agg.value += Number(row[metric]) || 0;
      agg.regions.push(row);
      perFeature.set(fi, agg);
    }
    let max = 0;
    for (const agg of perFeature.values()) max = Math.max(max, agg.value);
    return { perFeature, max: max || 1 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metric]);

  statsRef.current = stats;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    startTimeRef.current = performance.now();

    // ── DPR-aware canvas sizing. Размер берём у PARENT, а не у canvas:
    // глобус центрируется по доминирующей стороне (контейнер любой пропорции).
    const setupCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = parent.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      cssSizeRef.current = { w: rect.width, h: rect.height };
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    setupCanvas();
    window.addEventListener("resize", setupCanvas);
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(setupCanvas);
      ro.observe(canvas.parentElement);
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setupCanvas();
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onMouseLeave = () => { mouseRef.current = null; };
    const onTouch = (e) => {
      const t = e.touches[0];
      if (!t) return;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };
    const onTouchEnd = () => { mouseRef.current = null; };
    // Клик → выделенный регион (интерактивтілік үшін)
    const onActivate = () => {
      const fi = hoveredRef.current >= 0 ? OBLAST_FEATURE_IDX[hoveredRef.current] : -1;
      if (onSelect && fi >= 0) onSelect(fi);
    };
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseleave", onMouseLeave);
    canvas.addEventListener("touchstart", onTouch, { passive: true });
    canvas.addEventListener("touchmove", onTouch, { passive: true });
    canvas.addEventListener("touchend", onTouchEnd);
    canvas.addEventListener("touchcancel", onTouchEnd);
    canvas.addEventListener("click", onActivate);

    const cosX = Math.cos(AX), sinX = Math.sin(AX);

    function tick(now) {
      rafRef.current = requestAnimationFrame(tick);
      if (now - lastFrameRef.current < FRAME_MS) return;
      // Self-healing: если bitmap 0×0 (после transition / bfcache)
      if (!canvas.width || !canvas.height) {
        setupCanvas();
        if (!canvas.width || !canvas.height) return;
      }

      const dt = prevTimeRef.current
        ? Math.min((now - prevTimeRef.current) / 1000, 0.1)
        : 0.033;
      prevTimeRef.current = now;
      lastFrameRef.current = now;

      const t = (now - startTimeRef.current) / 1000;
      // Zoom-та 7° тербеліс тым үлкен ығысалу береді — 2° жеткілікті
      const ay = AY_BASE + 2 * DEG * Math.sin((2 * Math.PI / 40) * t);
      const cosY = Math.cos(ay), sinY = Math.sin(ay);

      const { w, h } = cssSizeRef.current;
      if (!w || !h) return;
      const cx = w / 2, cy = h / 2;
      // Қазақстан кадрды толтыратындай зум: глобус шеті контейнерден шығып
      // тұрады (ziyarat.kz KazakhstanGlobe әдісімен бірдей — радиус көбейткіші).
      // KZ экранның ~±0.33R × ±0.18R аумағын алады — екі шекті де қамтиды.
      const R = Math.min(w * 1.4, h * 2.3);

      function project([x, y, z]) {
        const rx_ = x * cosY + z * sinY;
        const rz0 = -x * sinY + z * cosY;
        return [-rx_, -(y * cosX - rz0 * sinX), y * sinX + rz0 * cosX];
      }

      const palette = darkRef.current ? DARK : LIGHT;
      const s = statsRef.current || { perFeature: new Map(), max: 1 };

      // Choropleth түсі: 0 → zero түс, max → max түс
      function oblastColor(fi) {
        const agg = s.perFeature.get(fi);
        // power-масштаб: маленькие значения тоже различимы, топ-регион не «выжигает» карту
        const p = agg ? Math.pow(agg.value / s.max, 0.65) : 0;
        return lerpColor(palette.zero, palette.max, p);
      }

      // ── Find hovered oblast (pip test in screen space) ──
      // Reverse: city enclaves (Almaty/Shymkent/Astana) win over surrounding oblasts.
      if (mouseRef.current) {
        const { x: mx, y: my } = mouseRef.current;
        hoveredRef.current = -1;
        for (let oi = N_OBLASTS - 1; oi >= 0; oi--) {
          const ring = KZ_OBLAST_BASE_3D[oi];
          const screenPts = ring.map((pt) => {
            const [px, py, pz] = project(pt);
            return pz >= 0.05 ? [cx + px * R, cy + py * R] : null;
          });
          if (pip2d(mx, my, screenPts)) { hoveredRef.current = oi; break; }
        }
        canvas.style.cursor = hoveredRef.current >= 0 ? "pointer" : "default";
      } else {
        hoveredRef.current = -1;
      }

      // ── Tooltip ──
      const tip = tooltipRef.current;
      const metricKey = metricRef.current;
      if (tip) {
        const hovFi = hoveredRef.current >= 0 ? OBLAST_FEATURE_IDX[hoveredRef.current] : -1;
        const agg = hovFi >= 0 ? s.perFeature.get(hovFi) : null;
        if (agg && mouseRef.current) {
          if (lastTooltipFeature.current !== hovFi) {
            lastTooltipFeature.current = hovFi;
            const rowsHtml = agg.regions
              .map(
                (r) =>
                  `<tr><td style="padding:2px 10px 2px 0;font-size:12px;opacity:0.75;">${r.name_ru || r.name_kz}</td>` +
                  `<td style="padding:2px 0;text-align:right;font-size:12px;font-weight:600;white-space:nowrap;">${(
                    Number(r[metricKey]) || 0
                  ).toLocaleString("ru-RU")}</td></tr>`
              )
              .join("");
            tip.innerHTML =
              `<div style="font-weight:600;font-size:12px;letter-spacing:0.04em;text-transform:uppercase;color:#6366F1;margin-bottom:6px;">${
                METRIC_LABELS[metricKey] || metricKey
              }</div>` +
              `<table style="border-collapse:collapse;">${rowsHtml}</table>` +
              `<div style="display:flex;gap:14px;margin-top:8px;padding-top:6px;border-top:1px solid rgba(99,102,241,0.3);font-size:11px;opacity:0.8;">` +
              `<span>👤 ${(agg.regions.reduce((a, r) => a + (Number(r.users) || 0), 0)).toLocaleString("ru-RU")}</span>` +
              `<span>📢 ${(agg.regions.reduce((a, r) => a + (Number(r.announcements) || 0), 0)).toLocaleString("ru-RU")}</span>` +
              `<span>👁 ${(agg.regions.reduce((a, r) => a + (Number(r.views) || 0), 0)).toLocaleString("ru-RU")}</span>` +
              `</div>`;
          }
          tip.style.opacity = "1";
          // Не даём tooltip выйти за границы контейнера:
          // по горизонтали — clamp, у верхнего края — «переворот» под курсор
          const pctX = Math.min(Math.max(mouseRef.current.x / w, 0.12), 0.88);
          const pctY = mouseRef.current.y / h;
          tip.style.left = pctX * 100 + "%";
          tip.style.top = pctY * 100 + "%";
          tip.style.transform =
            pctY < 0.32 ? "translate(-50%, 24px)" : "translate(-50%, calc(-100% - 22px))";
        } else {
          lastTooltipFeature.current = -1;
          tip.style.opacity = "0";
        }
      }

      // ── Animate oblast elevations (hover немесе highlight) ──
      const hlFi = highlightRef.current;
      for (let i = 0; i < N_OBLASTS; i++) {
        const fi = OBLAST_FEATURE_IDX[i];
        const target = i === hoveredRef.current || fi === hlFi ? 1 : 0;
        const delta = target - oblastElev.current[i];
        if (Math.abs(delta) < 0.002) oblastElev.current[i] = target;
        else oblastElev.current[i] += delta * ANIM_SPD * dt;
      }

      // ── Clear ──
      ctx.clearRect(0, 0, w, h);

      // Atmosphere ring
      const atm = ctx.createRadialGradient(cx, cy, R * 0.93, cx, cy, R * 1.1);
      atm.addColorStop(0, "rgba(99,102,241,0.05)");
      atm.addColorStop(1, "transparent");
      ctx.fillStyle = atm;
      ctx.fillRect(0, 0, w, h);

      // Globe disc tint
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(99,102,241,0.03)";
      ctx.fill();

      // Clip to globe
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R - 0.5, 0, Math.PI * 2);
      ctx.clip();

      // World fill
      ctx.fillStyle = palette.worldFill;
      for (const ring of WORLD_3D) {
        let started = false;
        ctx.beginPath();
        for (const pt of ring) {
          const [px, py, pz] = project(pt);
          if (pz < 0) {
            if (started) { ctx.closePath(); ctx.fill(); ctx.beginPath(); started = false; }
            continue;
          }
          const sx = cx + px * R, sy = cy + py * R;
          if (!started) { ctx.moveTo(sx, sy); started = true; }
          else ctx.lineTo(sx, sy);
        }
        if (started) { ctx.closePath(); ctx.fill(); }
      }

      // Lat/lon grid
      for (const { pts, isEquator } of GRID_3D) {
        for (let i = 0; i < pts.length - 1; i++) {
          const [px1, py1, pz1] = project(pts[i]);
          const [px2, py2, pz2] = project(pts[i + 1]);
          const vis = ((pz1 + pz2) / 2 + 1) / 2;
          if (vis < 0.15) continue;
          ctx.beginPath();
          ctx.moveTo(cx + px1 * R, cy + py1 * R);
          ctx.lineTo(cx + px2 * R, cy + py2 * R);
          ctx.strokeStyle = `rgba(${palette.grid},${vis * (isEquator ? 0.16 : 0.07)})`;
          ctx.lineWidth = isEquator ? 0.55 : 0.35;
          ctx.stroke();
        }
      }

      ctx.restore();

      // ── Flat Kazakhstan base fill ──
      ctx.beginPath();
      let startedKZ = false;
      for (const pt of KZ_BASE_3D) {
        const [px, py, pz] = project(pt);
        if (pz < 0.05) { startedKZ = false; continue; }
        const sx = cx + px * R, sy = cy + py * R;
        if (!startedKZ) { ctx.moveTo(sx, sy); startedKZ = true; }
        else ctx.lineTo(sx, sy);
      }
      ctx.closePath();
      ctx.fillStyle = palette.worldFill;
      ctx.fill();

      // ── Oblasts: choropleth fill + elevated when hovered/highlighted ──
      for (let oi = 0; oi < N_OBLASTS; oi++) {
        const fi = OBLAST_FEATURE_IDX[oi];
        const elev = oblastElev.current[oi];
        const currentAlt = KZ_ALT * elev;
        const ring = KZ_OBLAST_BASE_3D[oi];
        const N = ring.length;
        const topColor = oblastColor(fi);

        const baseProj = ring.map((pt) => project(pt));
        const topProj = ring.map((pt) => {
          const f = 1 + currentAlt;
          return project([pt[0] * f, pt[1] * f, pt[2] * f]);
        });

        // Solid backing at base level — fills corner gaps between wall quads
        ctx.beginPath();
        let bk = false;
        for (const [px, py, pz] of baseProj) {
          if (pz < 0.05) { bk = false; continue; }
          const sx = cx + px * R, sy = cy + py * R;
          if (!bk) { ctx.moveTo(sx, sy); bk = true; } else ctx.lineTo(sx, sy);
        }
        ctx.closePath();
        ctx.fillStyle = darken(topColor, 0.55);
        ctx.fill();

        // Walls — back-to-front (тек көтерілгенде ғана көрінеді)
        if (elev > 0.005) {
          const walls = [];
          for (let i = 0; i < N - 1; i++) {
            if (baseProj[i][2] < 0.05 && baseProj[i + 1][2] < 0.05) continue;
            walls.push([i, (baseProj[i][2] + baseProj[i + 1][2]) / 2]);
          }
          walls.sort((a, b) => a[1] - b[1]);

          ctx.fillStyle = ctx.strokeStyle = darken(topColor, 0.55);
          ctx.lineWidth = 1.2;
          ctx.lineJoin = "round";
          for (const [i] of walls) {
            const a = baseProj[i], b = baseProj[i + 1], c = topProj[i + 1], d = topProj[i];
            ctx.beginPath();
            ctx.moveTo(cx + a[0] * R, cy + a[1] * R);
            ctx.lineTo(cx + b[0] * R, cy + b[1] * R);
            ctx.lineTo(cx + c[0] * R, cy + c[1] * R);
            ctx.lineTo(cx + d[0] * R, cy + d[1] * R);
            ctx.closePath();
            ctx.fill(); ctx.stroke();
          }
        }

        // Top fill — hovered чуть ярче (lerp к белому)
        ctx.beginPath();
        let started = false;
        for (const [px, py, pz] of topProj) {
          if (pz < 0.05) {
            if (started) { ctx.closePath(); started = false; }
            continue;
          }
          const sx = cx + px * R, sy = cy + py * R;
          if (!started) { ctx.moveTo(sx, sy); started = true; }
          else ctx.lineTo(sx, sy);
        }
        if (started) ctx.closePath();
        ctx.fillStyle =
          oi === hoveredRef.current && elev > 0.3
            ? lerpColor([255, 255, 255], parseRgb(topColor), 0.3)
            : topColor;
        ctx.fill();
      }

      // ── Oblast border lines ──
      ctx.lineJoin = "round";
      for (let oi = 0; oi < N_OBLASTS; oi++) {
        const elev = oblastElev.current[oi];
        const isHov = oi === hoveredRef.current;
        const alt = KZ_ALT * elev + 0.001;
        ctx.strokeStyle = darkRef.current
          ? isHov ? "#A5B4FC" : "rgba(129,140,248,0.4)"
          : isHov ? "#4338CA" : "rgba(67,56,202,0.35)";
        ctx.lineWidth = isHov ? 1.0 : 0.65;
        ctx.beginPath();
        let started = false;
        for (const pt of KZ_OBLAST_BASE_3D[oi]) {
          const f = 1 + alt;
          const [px, py, pz] = project([pt[0] * f, pt[1] * f, pt[2] * f]);
          if (pz < 0.05) {
            if (started) { ctx.stroke(); ctx.beginPath(); started = false; }
            continue;
          }
          const sx = cx + px * R, sy = cy + py * R;
          if (!started) { ctx.moveTo(sx, sy); started = true; }
          else ctx.lineTo(sx, sy);
        }
        if (started) ctx.stroke();
      }

      // ── KZ outer border ──
      const passes = [[3.0, 0.18], [1.0, 0.9]];
      for (const [lw, alpha] of passes) {
        ctx.lineWidth = lw;
        ctx.beginPath();
        let started = false;
        for (const pt of KZ_BASE_3D) {
          const [px, py, pz] = project(pt);
          if (pz < 0.05) { started = false; continue; }
          const sx = cx + px * R, sy = cy + py * R;
          if (!started) { ctx.moveTo(sx, sy); started = true; }
          else ctx.lineTo(sx, sy);
        }
        ctx.strokeStyle = `rgba(99,102,241,${alpha})`;
        ctx.lineJoin = "round";
        ctx.stroke();
      }

      // Globe horizon
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(99,102,241,0.18)";
      ctx.lineWidth = 0.7;
      ctx.stroke();
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("resize", setupCanvas);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (ro) ro.disconnect();
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
      canvas.removeEventListener("touchstart", onTouch);
      canvas.removeEventListener("touchmove", onTouch);
      canvas.removeEventListener("touchend", onTouchEnd);
      canvas.removeEventListener("touchcancel", onTouchEnd);
      canvas.removeEventListener("click", onActivate);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelect]);

  const maxValue = [...stats.perFeature.values()].reduce((a, x) => Math.max(a, x.value), 0);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <canvas
        ref={canvasRef}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
      {/* Легенда */}
      <div
        style={{
          position: "absolute",
          left: 12,
          bottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 11,
          fontFamily: "inherit",
          color: dark ? "rgba(229,231,235,0.75)" : "rgba(55,65,81,0.75)",
          background: dark ? "rgba(17,24,39,0.75)" : "rgba(255,255,255,0.85)",
          borderRadius: 8,
          padding: "6px 10px",
          border: `1px solid ${dark ? "rgba(99,102,241,0.25)" : "rgba(99,102,241,0.2)"}`,
          pointerEvents: "none",
        }}
      >
        <span>0</span>
        <span
          style={{
            width: 90,
            height: 8,
            borderRadius: 4,
            // шкала карты нелинейная (value^0.65): середина значений
            // сидит на ~64% градиента, не на 50% — подпись это учитывает
            background: `linear-gradient(to right, ${lerpColor(
              dark ? DARK.zero : LIGHT.zero,
              dark ? DARK.max : LIGHT.max,
              0
            )}, ${lerpColor(dark ? DARK.zero : LIGHT.zero, dark ? DARK.max : LIGHT.max, 1)})`,
            position: "relative",
          }}
        >
          <span
            style={{
              position: "absolute",
              left: "63.7%",
              top: -2,
              width: 1,
              height: 12,
              background: dark ? "rgba(229,231,235,0.5)" : "rgba(55,65,81,0.5)",
            }}
          />
        </span>
        <span>{maxValue.toLocaleString("ru-RU")}</span>
        <span style={{ opacity: 0.65 }}>
          (50% ≈ {Math.round(maxValue / 2).toLocaleString("ru-RU")} — засечка)
        </span>
      </div>
      <div
        ref={tooltipRef}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          opacity: 0,
          pointerEvents: "none",
          background: dark ? "rgba(17, 24, 39, 0.95)" : "rgba(255, 255, 255, 0.97)",
          color: dark ? "#f3f4f6" : "#111827",
          border: `1px solid ${dark ? "rgba(99,102,241,0.35)" : "rgba(99,102,241,0.45)"}`,
          borderRadius: 12,
          padding: "10px 14px",
          minWidth: 180,
          maxWidth: 280,
          boxShadow: dark
            ? "0 10px 32px rgba(0,0,0,0.55)"
            : "0 10px 28px rgba(67,56,202,0.18)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          transform: "translate(-50%, calc(-100% - 22px))",
          transition: "opacity 0.18s ease-out",
          zIndex: 50,
          fontFamily: "inherit",
          lineHeight: 1.45,
        }}
      />
    </div>
  );
}

export default AnalyticsGlobe;